import {
    IPEDS_WIZARD_FETCH_SELECT_ITEMS_REQUEST,
    IPEDS_WIZARD_FETCH_SELECT_ITEMS_SUCCESS,
    IPEDS_WIZARD_FETCH_SELECT_ITEMS_FAILURE,
    IPEDS_WIZARD_SET_ACTIVE_STEP,
    IPEDS_WIZARD_SET_EXTRACT,
    IPEDS_WIZARD_SET_PARAMETER,
    IPEDS_WIZARD_GENERATE_REPORT_REQUEST,
    IPEDS_WIZARD_GENERATE_REPORT_SUCCESS,
    IPEDS_WIZARD_GENERATE_REPORT_FAILURE,
    IPEDS_WIZARD_RESET_PARAMETERS,
    IPEDS_WIZARD_RESET
} from './ipedsWizardTypes';
import { DEFAULT_OUTPUT_FORMAT, requiresTerms } from '../data/ipedsExtractMeta';

export const initialParameters = {
    reportingYearId: '',
    reportType: '',
    termIds: [],
    awardStatusIds: [],
    outputFormat: DEFAULT_OUTPUT_FORMAT
};

export const initialState = {
    isLoading: false,
    activeStep: 1,
    furthestStep: 1,
    extract: '',
    selectItems: {
        institutionName: '',
        academicYear: [],
        reportType: [],
        terms: [],
        awardStatus: []
    },
    parameters: { ...initialParameters },
    report: null,
    isGenerating: false,
    generateFailed: false
};

// The blank SelectItem the API prepends to Academic Year is a <select> placeholder; a
// type-ahead has its own empty state, so an option with no label would just be an
// unlabelled row the user can pick.
const withoutBlanks = (items) => (items || []).filter((item) => item && item.displayText);

const ipedsWizardReducer = (state = initialState, action = {}) => {
    switch (action.type) {
        case IPEDS_WIZARD_FETCH_SELECT_ITEMS_REQUEST:
            return { ...state, isLoading: true };

        case IPEDS_WIZARD_FETCH_SELECT_ITEMS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                selectItems: {
                    institutionName: action.payload?.institutionName || '',
                    academicYear: withoutBlanks(action.payload?.academicYear),
                    reportType: withoutBlanks(action.payload?.reportType),
                    terms: withoutBlanks(action.payload?.terms),
                    awardStatus: withoutBlanks(action.payload?.awardStatus)
                }
            };

        case IPEDS_WIZARD_FETCH_SELECT_ITEMS_FAILURE:
            return { ...state, isLoading: false };

        case IPEDS_WIZARD_SET_ACTIVE_STEP:
            return {
                ...state,
                activeStep: action.payload,
                furthestStep: Math.max(state.furthestStep, action.payload)
            };

        case IPEDS_WIZARD_SET_EXTRACT:
            return { ...state, extract: action.payload };

        case IPEDS_WIZARD_SET_PARAMETER: {
            const { field, value } = action.payload;
            const parameters = { ...state.parameters, [field]: value };
            // Terms only scope the Academic Reporter population. Leaving a stale selection
            // behind would silently narrow a Program or Military/Veteran run.
            if (field === 'reportType' && !requiresTerms(value)) {
                parameters.termIds = [];
            }
            // Any parameter change invalidates a report generated from the previous ones.
            return { ...state, parameters, report: null, generateFailed: false };
        }

        case IPEDS_WIZARD_GENERATE_REPORT_REQUEST:
            return { ...state, isGenerating: true, generateFailed: false };

        case IPEDS_WIZARD_GENERATE_REPORT_SUCCESS:
            return { ...state, isGenerating: false, generateFailed: false, report: action.payload };

        case IPEDS_WIZARD_GENERATE_REPORT_FAILURE:
            return { ...state, isGenerating: false, generateFailed: true, report: null };

        // Cancel: the acceptance criteria ask for the fields to reset, not for the wizard to
        // close, so the loaded select-items and the chosen extract survive.
        case IPEDS_WIZARD_RESET_PARAMETERS:
            return {
                ...state,
                parameters: { ...initialParameters },
                report: null,
                isGenerating: false,
                generateFailed: false,
                activeStep: 1,
                furthestStep: 1
            };

        case IPEDS_WIZARD_RESET:
            return { ...initialState, selectItems: state.selectItems };

        default:
            return state;
    }
};

export default ipedsWizardReducer;
